import styled from "styled-components";
import { useVerified } from "../../context/VerifiedContext";
import { VerificationBadge } from "./VerificationBadge";

/**
 * Renders a username with the appropriate verification badge inline.
 * Handles blue (verified individual), gold (organization), and purple (community founder).
 *
 * Props:
 *   name         - display name string
 *   uid          - Firebase UID of the user (for Firestore lookup)
 *   email        - user email (fallback for bootstrap seeded accounts)
 *   founderUid   - UID of the community founder (pass when rendering inside a community)
 *   showFounderBadge - if true, shows purple badge when uid === founderUid
 *   badgeSize    - "sm" | "md" | "lg" (default "sm")
 *   className    - optional styled-components class passthrough
 *   isSuperAdmin - optional override to show the SUPER_ADMIN pill
 */
export const UserNameWithBadge = ({
    name,
    uid,
    email,
    founderUid,
    className,
    badgeSize = "sm",
    showFounderBadge = false,
}) => {
    const { getBadgeType, isFounder } = useVerified();
    const badgeType = getBadgeType(uid, email);
    const showFounder = showFounderBadge && isFounder(uid, founderUid);

    return (
        <NameWrap className={className}>
            <span className="name-text">{name}</span>
            {badgeType && !showFounder && (
                <VerificationBadge type={badgeType} size={badgeSize} />
            )}
            {showFounder && (
                <VerificationBadge type="purple" size={badgeSize} />
            )}
        </NameWrap>
    );
};

const NameWrap = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 2px;

    .name-text {
        line-height: inherit;
        font-size: inherit;
        font-weight: inherit;
        color: inherit;
    }
`;

export default UserNameWithBadge;
